import React, { useState, useEffect, useRef } from "react";
import { mergeDecrypted } from "../utils/decrypt";
import {
  Table,
  Button,
  Input,
  Tag,
  Typography,
  Alert,
  Row,
  Col,
  Card,
  Modal,
  Tooltip,
  message,
} from "antd";
import { Search, Upload, Eye } from "lucide-react";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";

const { Title, Text } = Typography;

const Orders = () => {
  const { request } = useRequest();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [summary, setSummary] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchOrders(1, pagination.pageSize, searchText);
  }, []);

  const fetchOrders = (page = 1, pageSize = 10, search = "") => {
    setLoading(true);
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
    request({
      url: `${apiPath.getTotalOrderList}?page=${page}&page_size=${pageSize}${searchParam}`,
      method: "GET",
      onSuccess: async (data) => {
        const rawOrderList = Array.isArray(data)
          ? data
          : (data.data ?? data.results ?? []);

        // Decrypt each of the 10 paginated orders
        const orderList = await Promise.all(
          rawOrderList.map(async (order) => {
            try {
              return await mergeDecrypted(order);
            } catch (err) {
              console.error(`Failed to decrypt order id=${order.id}:`, err);
              return order;
            }
          }),
        );

        setOrders(orderList);

        // Update server pagination total and current page
        setPagination({
          current: data.current_page || page,
          pageSize: data.page_size || pageSize,
          total: data.total ?? rawOrderList.length,
        });

        // Set summary statistics directly from backend
        if (data.summary) {
          setSummary(data.summary);
        } else {
          const totalOrders = data.total || orderList.length;
          const totalPointsUsed = orderList.reduce(
            (sum, order) => sum + (parseFloat(order.total_amount) || 0),
            0,
          );
          setSummary({
            total_orders: totalOrders,
            total_points_used: totalPointsUsed,
            total_revenue: totalPointsUsed,
            last_7_days: 0,
          });
        }

        if (!orderList.length) {
          setErrorMessage("No orders found.");
        } else {
          setErrorMessage("");
        }
        setLoading(false);
      },
      onError: (err) => {
        console.error("Orders fetch error:", err);
        setErrorMessage(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load orders.",
        );
        setLoading(false);
      },
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "#10b981";
      case "pending":
        return "#f59e0b";
      case "cancelled":
        return "#ef4444";
      case "processing":
        return "#3b82f6";
      default:
        return "#94a3b8";
    }
  };

  const getStatusText = (status) => {
    return status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown";
  };

  // Transform orders to data source
  const getDataSource = () => {
    return orders.map((order, index) => {
      const itemCount =
        order.items_count !== undefined && order.items_count !== null
          ? order.items_count
          : (order.items?.length || 0);
      const srNo = (pagination.current - 1) * pagination.pageSize + index + 1;

      return {
        key: order.id || index,
        srNo: srNo,
        orderId: order.order_number,
        id: order.id,
        date: formatDate(order.created_at),
        status: order.status,
        totalAmount: parseFloat(order.total_amount || 0).toFixed(2),
        paymentMethod: order.payment_method?.toUpperCase() || "POINTS",
        customerName:
          order.customer_name || order.name || order.user?.name || "Unknown",
        email: order.customer_email || order.email || order.user?.email || "-",
        location:
          [
            order.shipping_address_line1,
            order.shipping_city,
            order.shipping_state,
            order.shipping_zipcode,
            order.shipping_country,
          ]
            .filter(Boolean)
            .join(", ") || "N/A",
        itemCount: itemCount,
        notes: order.notes,
        paymentStatus: order.payment_status,
        shippingAddress: {
          line1: order.shipping_address_line1,
          line2: order.shipping_address_line2,
          city: order.shipping_city,
          state: order.shipping_state,
          country: order.shipping_country,
          zipcode: order.shipping_zipcode,
        },
        items: order.items || [],
        phone:
          order.customer_phone ||
          order.phone_number ||
          order.phone ||
          order.user?.phone_number ||
          "-",
        createdAt: order.created_at,
      };
    });
  };

  const handleTableChange = (newPagination) => {
    fetchOrders(newPagination.current, newPagination.pageSize, searchText);
  };

  const handleViewDetails = (record) => {
    setSelectedOrder(record);
    setModalVisible(true);
  };

  const columns = [
    {
      title: "SR No.",
      dataIndex: "srNo",
      key: "srNo",
      width: 70,
      fixed: "left",
      render: (text) => (
        <Text style={{ color: "#64748b", fontWeight: 500 }}>{text}</Text>
      ),
    },
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      width: 220,
      render: (text, record) => (
        <div>
          <Text strong style={{ color: "#0f172a", fontSize: "13px" }}>
            {text}
          </Text>
          <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
            ID: {record.id}
          </div>
        </div>
      ),
    },
    {
      title: "Date & Time",
      dataIndex: "date",
      key: "date",
      width: 160,
      render: (text) => (
        <Text style={{ color: "#64748b", fontSize: "13px" }}>{text}</Text>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      width: 180,
      render: (text, record) => (
        <div>
          <Text style={{ color: "#334155", fontSize: "13px", fontWeight: 500 }}>
            {text}
          </Text>
          <div style={{ fontSize: "12px", color: "#64748b" }}>
            {record.email}
          </div>
        </div>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 120,
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: "#0f172a", fontSize: "14px" }}>
          {amount} pts
        </Text>
      ),
    },
    {
      title: "Payment",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      width: 100,
      render: (method, record) => (
        <div>
          <Tag color="purple" style={{ borderRadius: "4px" }}>
            {method}
          </Tag>
          <div
            style={{
              fontSize: "11px",
              color: record.paymentStatus === "paid" ? "#10b981" : "#f59e0b",
              marginTop: "4px",
            }}
          >
            {record.paymentStatus}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => (
        <Tag
          color="success"
          style={{
            borderRadius: "12px",
            border: "none",
            fontWeight: 500,
            padding: "2px 12px",
            background: getStatusColor(status),
            color: "#fff",
            fontSize: "12px",
          }}
        >
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: "Items",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 80,
      align: "center",
      render: (count) => (
        <div
          style={{
            background: "#f1f5f9",
            borderRadius: "20px",
            padding: "2px 10px",
            display: "inline-block",
            fontSize: "13px",
            fontWeight: 500,
            color: "#475569",
          }}
        >
          {count} item{count !== 1 ? "s" : ""}
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 220,
      ellipsis: true,
      render: (location) => (
        <Text style={{ color: "#64748b", fontSize: "12px" }} ellipsis>
          {location}
        </Text>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button
            type="text"
            icon={<Eye size={18} style={{ color: "#2563eb" }} />}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(record);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  // Export to CSV with better formatting
  const handleExport = () => {
    const dataSource = getDataSource();
    const exportData = dataSource.map((order, index) => ({
      "SR No.": index + 1,
      "Order ID": order.orderId,
      Date: order.date,
      "Customer Name": order.customerName,
      Email: order.email,
      Phone: order.phone || "-",
      "Total Amount (Points)": order.totalAmount,
      "Payment Method": order.paymentMethod,
      "Payment Status": order.paymentStatus,
      "Order Status": getStatusText(order.status),
      "Items Count": order.itemCount,
      Location: order.location,
      Notes: order.notes || "-",
    }));

    const csv = convertToCSV(exportData);
    // Add BOM for UTF-8 to handle special characters
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19);
    a.href = url;
    a.download = `orders_export_${timestamp}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    // Show success message
    message.success(`${exportData.length} orders exported successfully`);
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    const headers = Object.keys(data[0]);
    const csvRows = [];

    csvRows.push(headers.join(","));

    for (const row of data) {
      const values = headers.map((header) => {
        let value = row[header] || "";
        // Convert to string and escape quotes
        value = String(value);
        if (
          value.includes(",") ||
          value.includes('"') ||
          value.includes("\n")
        ) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      });
      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  };

  const dataSource = getDataSource();

  return (
    <div
      className="orders-container"
      style={{ padding: "24px", background: "#fff", borderRadius: "10px" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <Title
          level={3}
          style={{ margin: 0, color: "#0f172a", fontWeight: 700 }}
        >
          Orders
        </Title>
        <Button
          icon={<Upload size={16} />}
          onClick={handleExport}
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: "6px",
            color: "#64748b",
            fontWeight: 500,
          }}
          disabled={dataSource.length === 0}
        >
          Export {pagination.total > 0 && `(${pagination.total})`}
        </Button>
      </div>

      {errorMessage && (
        <Alert
          type="error"
          message="Orders load error"
          description={errorMessage}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {summary && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24} md={12} xl={6}>
            <Card
              bordered
              style={{
                borderRadius: 12,
                boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
              }}
            >
              <Text style={{ color: "#64748b", fontSize: "14px" }}>
                Total Orders
              </Text>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#0f172a",
                }}
              >
                {summary.total_orders}
              </div>
            </Card>
          </Col>
          <Col span={24} md={12} xl={6}>
            <Card
              bordered
              style={{
                borderRadius: 12,
                boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
              }}
            >
              <Text style={{ color: "#64748b", fontSize: "14px" }}>
                Total Points Used
              </Text>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#8b5cf6",
                }}
              >
                {summary.total_points_used.toFixed(2)} pts
              </div>
            </Card>
          </Col>
          <Col span={24} md={12} xl={6}>
            <Card
              bordered
              style={{
                borderRadius: 12,
                boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
              }}
            >
              <Text style={{ color: "#64748b", fontSize: "14px" }}>
                Total points
              </Text>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#10b981",
                }}
              >
                {summary.total_revenue.toFixed(2)} pts
              </div>
            </Card>
          </Col>
          <Col span={24} md={12} xl={6}>
            <Card
              bordered
              style={{
                borderRadius: 12,
                boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
              }}
            >
              <Text style={{ color: "#64748b", fontSize: "14px" }}>
                Last 7 Days
              </Text>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#3b82f6",
                }}
              >
                {summary.last_7_days}
              </div>
            </Card>
          </Col>
        </Row>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <Input
          placeholder="Search by Order ID, Customer or Email"
          prefix={<Search size={16} color="#94a3b8" />}
          value={searchText}
          onChange={(e) => {
            const val = e.target.value;
            setSearchText(val);
            if (searchTimeoutRef.current) {
              clearTimeout(searchTimeoutRef.current);
            }
            searchTimeoutRef.current = setTimeout(() => {
              fetchOrders(1, pagination.pageSize, val);
            }, 400);
          }}
          style={{ width: 350, borderRadius: "8px" }}
          allowClear
          size="large"
        />
        <div>
          <Text type="secondary">
            Showing {orders.length} of {pagination.total} orders
          </Text>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} orders`,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={handleTableChange}
        loading={loading}
        scroll={{ x: 1400 }}
        locale={{ emptyText: errorMessage || "No orders found" }}
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          overflow: "hidden",
        }}
        rowClassName={() => "order-table-row"}
      />

      {/* Order Details Modal - Keep the same */}
      <Modal
        title={
          <div>
            <Text strong style={{ fontSize: "18px" }}>
              Order Details
            </Text>
            <div
              style={{ fontSize: "14px", color: "#64748b", marginTop: "4px" }}
            >
              {selectedOrder?.orderId}
            </div>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedOrder && (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={24} md={12}>
                <Card size="small" title="Order Information">
                  <div>
                    <strong>SR No.:</strong> {selectedOrder.srNo}
                  </div>
                  <div>
                    <strong>Order ID:</strong> {selectedOrder.orderId}
                  </div>
                  <div>
                    <strong>Date:</strong> {selectedOrder.date}
                  </div>
                  <div>
                    <strong>Status:</strong>
                    <Tag
                      style={{
                        marginLeft: 8,
                        borderRadius: "12px",
                        background: getStatusColor(selectedOrder.status),
                        color: "#fff",
                        border: "none",
                      }}
                    >
                      {getStatusText(selectedOrder.status)}
                    </Tag>
                  </div>
                  <div>
                    <strong>Total Amount:</strong> {selectedOrder.totalAmount}{" "}
                    pts
                  </div>
                  <div>
                    <strong>Payment Method:</strong>{" "}
                    {selectedOrder.paymentMethod}
                  </div>
                  <div>
                    <strong>Payment Status:</strong>
                    <span
                      style={{
                        color:
                          selectedOrder.paymentStatus === "paid"
                            ? "#10b981"
                            : "#f59e0b",
                      }}
                    >
                      {selectedOrder.paymentStatus}
                    </span>
                  </div>
                  {selectedOrder.notes && (
                    <div>
                      <strong>Notes:</strong> {selectedOrder.notes}
                    </div>
                  )}
                </Card>
              </Col>
              <Col span={24} md={12}>
                <Card size="small" title="Customer Information">
                  <div>
                    <strong>Name:</strong> {selectedOrder.customerName}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedOrder.email}
                  </div>
                  {selectedOrder.phone && (
                    <div>
                      <strong>Phone:</strong> {selectedOrder.phone}
                    </div>
                  )}
                </Card>
              </Col>
            </Row>

            <Card
              size="small"
              title="Shipping Address"
              style={{ marginBottom: 24 }}
            >
              <div>{selectedOrder.shippingAddress?.line1 || "N/A"}</div>
              {selectedOrder.shippingAddress?.line2 && (
                <div>{selectedOrder.shippingAddress.line2}</div>
              )}
              <div>
                {selectedOrder.shippingAddress?.city &&
                  `${selectedOrder.shippingAddress.city}, `}
                {selectedOrder.shippingAddress?.state &&
                  `${selectedOrder.shippingAddress.state} `}
                {selectedOrder.shippingAddress?.zipcode}
              </div>
              <div>{selectedOrder.shippingAddress?.country || "N/A"}</div>
            </Card>

            <Card size="small" title="Order Items">
              <Table
                dataSource={selectedOrder.items}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "SR No.",
                    key: "srNo",
                    width: 70,
                    render: (_, __, index) => index + 1,
                  },
                  {
                    title: "Product Name",
                    dataIndex: "product_name",
                    key: "product_name",
                    render: (text, record) =>
                      text || record.product?.name || record.name || "Product",
                  },
                  {
                    title: "Quantity",
                    dataIndex: "quantity",
                    key: "quantity",
                    width: 100,
                    align: "center",
                  },
                  {
                    title: "Line Total",
                    dataIndex: "line_total",
                    key: "line_total",
                    width: 120,
                    align: "right",
                    render: (total) =>
                      total ? `${parseFloat(total).toFixed(2)} pts` : "-",
                  },
                ]}
                rowKey="id"
              />
            </Card>
          </div>
        )}
      </Modal>

      <style jsx>{`
        .order-table-row {
          cursor: pointer;
          transition: background 0.3s;
        }
        .order-table-row:hover {
          background: #f8fafc;
        }
      `}</style>
    </div>
  );
};

export default Orders;
